import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, isColorDark, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

export default function ProductGridSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'grid';
    const columns = config.columns || 3;
    const currency = content.currency || '$';
    const items = (content.items || []).map(item => ({ ...item, ...resolveItemMedia(item, mediaMode, mediaFolders) }));

    const INITIAL_ITEMS = 24;
    const ITEMS_PER_PAGE = 24;
    const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState('Todos');

    if (items.length === 0) return null;

    const categories = ['Todos', ...Array.from(new Set(items.map(i => i.category).filter(Boolean)))];

    const handleSearch = (val) => { setSearchQuery(val); setVisibleCount(INITIAL_ITEMS); };
    const handleCategory = (cat) => { setActiveCategory(cat); setVisibleCount(INITIAL_ITEMS); };

    const filtered = items.filter(item => {
        const matchCat = activeCategory === 'Todos' || item.category === activeCategory;
        const matchSearch = !searchQuery || (item.name || '').toLowerCase().includes(searchQuery.toLowerCase());
        return matchCat && matchSearch;
    });

    const paginatedFiltered = filtered.slice(0, visibleCount);

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const gridCls = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    const hasDiscount = (item) => item.original_price && parseFloat(item.original_price) > parseFloat(item.price || 0);

    // ── List variant ──
    if (variant === 'list') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Productos'} subtitle={content.subtitle} config={config} s={s} />

                    {/* Search */}
                    {config.show_search && (
                        <div className="max-w-md mx-auto mb-6 relative">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input type="text" value={searchQuery} onChange={e => handleSearch(e.target.value)}
                                placeholder="Buscar productos..."
                                className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2"
                                style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb', color: s.text }} />
                        </div>
                    )}

                    {/* Category filter */}
                    {config.show_filter && categories.length > 1 && (
                        <div className="flex flex-wrap justify-center gap-2 mb-8">
                            {categories.map(cat => (
                                <button key={cat} onClick={() => handleCategory(cat)}
                                    className="px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                                    style={activeCategory === cat
                                        ? { backgroundColor: s.accent, color: '#ffffff' }
                                        : { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }
                                    }>
                                    {cat}
                                </button>
                            ))}
                        </div>
                    )}

                    {filtered.length === 0 ? (
                        <p className="text-center text-sm py-8" style={{ color: s.textSub }}>No se encontraron productos</p>
                    ) : (
                        <div className="space-y-4 max-w-4xl mx-auto">
                            {paginatedFiltered.map((item, i) => {
                                return (
                                    <div key={i} className={`${card} flex flex-col sm:flex-row items-center gap-5 p-5 transition-all duration-500 hover:-translate-y-0.5 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                                        style={{ transitionDelay: visible ? `${100 + Math.min(i, 10) * 80}ms` : '0ms' }}>
                                        <div className="relative w-full sm:w-32 h-32 shrink-0 rounded-lg overflow-hidden">
                                            <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-square" rounded="rounded-lg" />
                                            {item.badge && (
                                                <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: s.accent }}>{item.badge}</span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-base" style={{ color: s.text }}>{item.name}</h3>
                                            {item.category && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full mt-1 inline-block" style={{ backgroundColor: hexToRgba(s.secondary, 0.1), color: s.secondary }}>{item.category}</span>}
                                            {item.description && <p className="text-sm mt-1.5 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                        </div>
                                        <div className="shrink-0 flex flex-col items-end gap-2">
                                            <div className="text-right">
                                                <span className="font-bold text-xl" style={{ color: s.accent }}>{currency} {item.price}</span>
                                                {hasDiscount(item) && <span className="block text-xs line-through" style={{ color: s.textSub }}>{currency} {item.original_price}</span>}
                                            </div>
                                            {item.cta_text && (
                                                <a {...resolveLinkProps(item.cta_link || '#contact')} className="text-xs font-semibold px-4 py-2 rounded-lg transition-all hover:opacity-90 hover:scale-105"
                                                    style={{ backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }}>
                                                    {item.cta_text}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {visibleCount < filtered.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, filtered.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({filtered.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Grid (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Productos'} subtitle={content.subtitle} config={config} s={s} />

                {/* Search */}
                {config.show_search && (
                    <div className="max-w-md mx-auto mb-6 relative">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input type="text" value={searchQuery} onChange={e => handleSearch(e.target.value)}
                            placeholder="Buscar productos..."
                            className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2"
                            style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb', color: s.text }} />
                    </div>
                )}

                {/* Category filter */}
                {config.show_filter && categories.length > 1 && (
                    <div className="flex flex-wrap justify-center gap-2 mb-8">
                        {categories.map(cat => (
                            <button key={cat} onClick={() => handleCategory(cat)}
                                className="px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                                style={activeCategory === cat
                                    ? { backgroundColor: s.accent, color: '#ffffff' }
                                    : { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }
                                }>
                                {cat}
                            </button>
                        ))}
                    </div>
                )}

                {filtered.length === 0 ? (
                    <p className="text-center text-sm py-8" style={{ color: s.textSub }}>No se encontraron productos</p>
                ) : (
                    <div className={`grid grid-cols-2 ${gridCls} gap-6`}>
                        {paginatedFiltered.map((item, i) => {
                            return (
                                <div key={i} className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${150 + Math.min(i, 10) * 100}ms` : '0ms' }}>
                                    <div className="relative aspect-square overflow-hidden">
                                        <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-square" rounded="" />
                                        {item.badge && (
                                            <span className="absolute top-3 right-3 text-[10px] font-bold px-2.5 py-1 rounded-full text-white shadow-lg" style={{ backgroundColor: s.accent }}>{item.badge}</span>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        {item.category && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.secondary, 0.1), color: s.secondary }}>{item.category}</span>}
                                        <h3 className="font-bold text-sm mt-1.5" style={{ color: s.text }}>{item.name}</h3>
                                        {item.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="flex items-baseline gap-2">
                                                <span className="font-bold text-lg" style={{ color: s.accent }}>{currency} {item.price}</span>
                                                {hasDiscount(item) && <span className="text-xs line-through" style={{ color: s.textSub }}>{currency} {item.original_price}</span>}
                                            </div>
                                            {item.cta_text && (
                                                <a {...resolveLinkProps(item.cta_link || '#contact')} className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-90 hover:scale-105"
                                                    style={{ backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }}>
                                                    {item.cta_text}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {visibleCount < filtered.length && (
                    <div className="text-center mt-8">
                        <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, filtered.length))}
                            className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                            style={{borderColor: s.accent, color: s.accent}}>
                            Ver mas ({filtered.length - visibleCount} restantes)
                        </button>
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
